import type RealtimeKitClient from "@cloudflare/realtimekit";

type SelfWithSpeakerApi = RealtimeKitClient["self"] & {
    setupSpeaker?: () => Promise<void>;
    getSpeakerDevices?: () => Promise<MediaDeviceInfo[]>;
    setSpeakerDevice?: (
        device: MediaDeviceInfo,
        opts: { saveDevicePreference: boolean },
    ) => Promise<void>;
    setDevice?: (device: MediaDeviceInfo) => Promise<void>;
    getCurrentDevices?: () => {
        audio?: MediaDeviceInfo;
        video?: MediaDeviceInfo;
        speaker?: MediaDeviceInfo;
    };
};

export type MobileSpeakerOptions = {
    /** iOS: pulso rápido mic off/on após gesto do usuário (re-rota áudio). */
    iosMicPulse?: boolean;
    root?: ParentNode | null;
};

/**
 * Garante volume máximo e reprodução ativa nos elementos de mídia da sala.
 * iOS/Android podem pausar ou abafar `<audio>/<video>` quando a aba Cenário
 * está ativa e o player RTK fica invisível.
 */
export function boostRemoteMediaElements(root?: ParentNode | null): void {
    if (typeof document === "undefined") {
        return;
    }

    const scope =
        (root instanceof HTMLElement ? root : null) ??
        document.querySelector(".meeting-room-shell");

    if (!scope) {
        return;
    }

    scope.querySelectorAll("audio, video").forEach((node) => {
        const media = node as HTMLMediaElement;
        media.volume = 1;
        media.muted = false;
        media.setAttribute("playsinline", "");
        media.setAttribute("webkit-playsinline", "true");
        if (media.paused) {
            void media.play().catch(() => undefined);
        }
    });
}

async function pickSpeakerDevice(speakers: MediaDeviceInfo[]): Promise<MediaDeviceInfo | null> {
    if (!speakers.length) {
        return null;
    }

    const preferred =
        speakers.find((d) => /speaker|alto|loud|external|default/i.test(d.label)) ??
        speakers.find((d) => d.deviceId === "default") ??
        speakers[0];

    return preferred ?? null;
}

async function applySpeakerDevice(self: SelfWithSpeakerApi): Promise<boolean> {
    if (typeof self.getSpeakerDevices !== "function") {
        return false;
    }

    const speakers = await self.getSpeakerDevices();
    const preferred = await pickSpeakerDevice(speakers);
    if (!preferred) {
        return false;
    }

    if (typeof self.setSpeakerDevice === "function") {
        await self.setSpeakerDevice(preferred, { saveDevicePreference: false });
        return true;
    }

    if (typeof self.setDevice === "function") {
        await self.setDevice(preferred);
        return true;
    }

    return false;
}

/**
 * iOS Safari costuma rotear WebRTC para o auricular após getUserMedia.
 * Um pulso rápido do mic (só após toque do usuário) às vezes força o alto-falante.
 */
async function iosMicRoutePulse(meetingClient: RealtimeKitClient): Promise<void> {
    const self = meetingClient.self;
    if (!self?.audioEnabled) {
        return;
    }

    try {
        await self.disableAudio();
        await new Promise((resolve) => window.setTimeout(resolve, 120));
        await self.enableAudio();
    } catch {
        // best-effort
    }
}

/**
 * No mobile, iOS/Android tendem a rotear WebRTC para o auricular quando o
 * player de vídeo/áudio está oculto (aba Cenário). Reaplica saída preferida.
 */
export async function ensureMobileSpeakerOutput(
    meetingClient: RealtimeKitClient | null,
    options: MobileSpeakerOptions = {},
): Promise<void> {
    if (!meetingClient) {
        return;
    }

    const self = meetingClient.self as SelfWithSpeakerApi;

    try {
        if (typeof self.setupSpeaker === "function") {
            await self.setupSpeaker();
        } else {
            await applySpeakerDevice(self);
        }
    } catch {
        try {
            await applySpeakerDevice(self);
        } catch {
            // continua com boost DOM abaixo
        }
    }

    boostRemoteMediaElements(options.root);

    const isIos =
        typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (options.iosMicPulse && isIos) {
        await iosMicRoutePulse(meetingClient);
        boostRemoteMediaElements(options.root);
    }
}
